/* HaGroupViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./HaGroupViewRenderer");
const PagingDeviceFeed = require("./PagingDeviceFeed");
const _ = require("lodash");

class HaGroupViewComponent extends EventEmitter {
    constructor(deviceDetails) {
        super();
        this._deviceDetails = deviceDetails;
        this._gameEditor = deviceDetails._gameEditor;
        this._modalUserFeedback = deviceDetails._modalUserFeedback;
        this._pageSize = 100;
        this._gridKey = "ha_device";
        this._logger = deviceDetails._logger;
        this._device = this._gameEditor.getDevice();
        this._haPairFeed = deviceDetails._haPairFeed;
        this._isDirty = false;
        this._userGridData = undefined;
        this._renderer = new Renderer(this);
        this._pairedDevice = undefined;
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    shutdown() {
        return Promise.all([this._availableDevicesPagingFeed.shutdown(), this.isDirtyChecked()])
            .then(() => {
                this.emit("Shutdown");
                this._unregisterListeners();
                this._availableDevicesPagingFeed = undefined;
                this._userGridData = undefined;
                this._pairedDevice = undefined;
                Promise.resolve();
            })
            .catch((err) => {
                console.error(err);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    get pairedDevice() {
        return this._pairedDevice;
    }

    set pairedDevice(newValue) {
        this._pairedDevice = newValue;
        return true;
    }
    get gameEditor() {
        return this._gameEditor;
    }

    get getDeviceDetailsView() {
        return this._device;
    }

    get availColumnConfig() {
        return {
            name: {
                label: "Device Name",
                entityColumn: "name"
            },
            description: {
                label: "Description",
                entityColumn: "description"
            },
            siteCodeName: {
                label: "Site Code Name",
                entityColumn: "siteCodeName"
            }
        };
    }

    get availableTargetsPagingFeed() {
        return this._availableDevicesPagingFeed;
    }

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    wasFeedPreviouslyInitialized() {
        return this._availableDevicesPagingFeed.wasPreviouslyInitialized();
    }

    whenFeedReady() {
        return this._deviceDetails._storageFactory
            .whenStorageIsReady()
            .then(() => {
                this._userGridData = this._deviceDetails._storageFactory.getRemoteStorageInstanceForEntityGrid("entity_grid_gl", `${this._gridKey}`);
            })
            .then(() => {
                this._createAvailPagingFeed();
                let hapair = _.get(this._haPairFeed, "cache", {});
                this._pairedDevice = Object.keys(hapair);
                return Promise.resolve();
            })
            .then(() => {
                return Promise.all([this._availableDevicesPagingFeed.whenFeedReady()]);
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this.shutdown();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/
    _createAvailPagingFeed() {
        if (this._availableDevicesPagingFeed) {
            this._availableDevicesPagingFeed.shutdown();
        }
        this._availableDevicesPagingFeed = this._createPagingFeed(this.availColumnConfig, `${this._gridKey}`);
    }
    _createPagingFeed(columnConfig, feedKey) {
        let oldFilterQuery = _.get(this._userGridData.getBasicSearchData(), "filterQuery", {});
        let filterQuery = this._filterQuery(oldFilterQuery);
        return new PagingDeviceFeed({
            activeOrgId: this._gameEditor._activeOrgId,
            entityType: "Device",
            entityDef: this._gameEditor._DeviceEntity,
            columnConfig: columnConfig,
            pageSize: this._pageSize,
            sort: { name: 1 },
            filter: filterQuery,
            feedKey
        });
    }
    _addRemoveHaPair(ids) {
        if (_.isEmpty(this._device.getHaPairName() || "") && ids.length === 0) {
            this._device.clearHaPairRelationship();
            if (!this._device.isDirty()) {
                this._deviceDetails._renderer._isDirty = false;
                this._deviceDetails._gameEditor.getTabCmpnt().setDirty(false);
            }
            return Promise.resolve();
        }
        if (!_.isEqual(this._pairedDevice, ids)) {
            this._pairedDevice = ids;
            this._gameEditor.clearPendingRelationshipsFor("haPair", this._device);
            if (ids.length > 0) {
                this._device.setRelatedHaPair(ids[0]);
            } else {
                this._device.setProperty("haPairName", ""); //when there is no selection, update details haPairName
            }
            this._deviceDetails._renderer._hideViewActions();
        }
    }
    resetSelection(newIds) {
        this.renderer._availableTargetsGrid.setSelectedRows(newIds);
    }

    loadAndScrollTo(entityId) {
        let that = this;
        this._availableDevicesPagingFeed.on("RequestedEntityFound", handleRequestedEntityFound);
        this._availableDevicesPagingFeed._requestEntity(entityId);
        function handleRequestedEntityFound(index) {
            that.renderer._availableTargetsGrid.renderer.scrollToTheRow(index);
            that._availableDevicesPagingFeed.removeListener("RefreshView", handleRequestedEntityFound);
            that._availableDevicesPagingFeed.removeListener("RequestedEntityFound", handleRequestedEntityFound);
        }
    }
    _filterQuery(filterQuery) {
        let haCache = _.get(this._haPairFeed, "cache", {});
        let filter;
        if (!_.isEmpty(haCache)) {
            filter = {
                $or: [
                    {
                        id: { $in: Object.keys(haCache) }
                    },
                    {
                        $and: [{ id: { $nin: [this._device._id] } }, { haPairName: { $exists: false } }]
                    }
                ]
            };
        } else {
            filter = {
                $or: [
                    {
                        $and: [{ id: { $nin: [this._device._id] } }, { haPairName: { $exists: false } }]
                    }
                ]
            };
        }
        let oldQuery = _.clone(filterQuery);
        let finalQuery = {};
        if (!_.isEmpty(oldQuery)) {
            finalQuery.$and = [oldQuery, filter];
        } else {
            finalQuery = filter;
        }
        filterQuery = {
            $userQuery: finalQuery,
            $systemQuery: { managementState: { $in: ["MANAGED", "UNMANAGED", "INVENTORY_ONLY"] } }
        };
        return filterQuery;
    }
    isDirtyChecked() {
        return Promise.resolve();
    }

    _getDeviceNameById(deviceId) {
        return this._gameEditor._DeviceEntity.retrieveDevice(this._gameEditor._activeOrgId, deviceId, { name: true });
    }
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /*******************************************/
}
module.exports = HaGroupViewComponent;
